<script setup>
import { computed, ref } from 'vue'

import { useSettingsStore } from '@/stores/SettingsStore.js'
import { useTableStore } from '@/stores/TableStore.js'
import { useParticipateStore } from '@/stores/ParticipateStore.js'

const settingsStore = useSettingsStore()
const tableStore = useTableStore()
const participateStore = useParticipateStore()

const props = defineProps({
    table: {
        type: Object,
        default: {
            participates: []
        },
        required: true
    },
    number: {
        type: Number,
        default: 0,
        required: true
    }
})

const tableMembers = computed(() => {
    let membersList = []

    let numberToShow = settingsStore.tableSize
    if (props.table.participates.length > numberToShow) {
        numberToShow = props.table.participates.length
    }

    for (var i = 0; i < numberToShow; i++) {
        if (props.table.participates[i]) {
            membersList.push(participateStore.get(props.table.participates[i]))
        } else {
            membersList.push(null)
        }
    }
    return membersList
})

function removeTable(id) {
    tableStore.remove(id)
}

function startDrag(event, participateID) {
    if (participateID) {
        settingsStore.showAvailableSlots = true
        event.dataTransfer.dropEffect = 'move'
        event.dataTransfer.effectAllowed = 'move'
        event.dataTransfer.setData('participateID', participateID)
    }
}

function endDrag(event) {
    settingsStore.showAvailableSlots = false
    event.dataTransfer.setData('participateID', null)
}

function onDrop(event, tableID) {
    let participateID = event.dataTransfer.getData('participateID')
    if (participateID) {
        tableStore.move(tableID, participateID)
    }
}

function toggleLock(participateID) {
    if (participateID) {
        participateStore.toggleLock(participateID)
    }
}

function toggleTableLock(currentValue) {
    props.table.participates.forEach((participateID) => {
        participateStore.toggleLock(participateID, !currentValue)
    })
}

const tableLabel = computed(() =>
    props.table.name?.trim() || `Table ${props.number + settingsStore.tableCountStart}`
)

const isEditingName = ref(false)
const draftName = ref('')

function startRename() {
    draftName.value = props.table.name ?? ''
    isEditingName.value = true
}

function commitRename() {
    tableStore.rename(props.table.uuid, draftName.value.trim())
    isEditingName.value = false
}

function cancelRename() {
    isEditingName.value = false
}

const isTableLocked = computed(() => {
    let result = true

    props.table.participates.forEach((participateID) => {
        let participate = participateStore.get(participateID)
        if (participate.lock == false) {
            result = false
        }
    })

    return result
})
</script>

<template>
    <div class="card" @drop="onDrop($event, table.uuid)" @dragenter.prevent @dragover.prevent :key="table.uuid">
        <div class="card-header">
            <div class="hstack gap-2">
                <div class="me-auto">
                    <span v-if="!isEditingName"
                          @dblclick="startRename"
                          title="Double-click to rename"
                          style="cursor: pointer;">
                        <strong>{{ tableLabel }}</strong>
                    </span>
                    <div v-else class="input-group input-group-sm" style="max-width: 12rem;">
                        <input type="text"
                               class="form-control form-control-sm"
                               v-model="draftName"
                               :placeholder="`Table ${number + settingsStore.tableCountStart}`"
                               @keyup.enter="commitRename"
                               @keyup.escape="cancelRename"
                               @blur="commitRename"
                               autofocus>
                    </div>
                </div>

                <div>
                    <span class="badge"
                          :class="table.participates.length >= settingsStore.tableSize ? 'text-bg-success' : 'text-bg-secondary'">
                        {{ table.participates.length }}/{{ settingsStore.tableSize }}
                    </span>
                </div>

                <div class="ms-1" v-if="table.participates.length === 0 && settingsStore.showOptions === true">
                    <button type="button" class="btn btn-danger btn-sm" @click="removeTable(table.uuid)">Remove</button>
                </div>
                <div class="ms-1" v-if="table.participates.length !== 0 && settingsStore.showOptions === true">
                    <button type="button" class="ms-auto btn btn-sm" @click="toggleTableLock(isTableLocked)">
                        <font-awesome-icon class="" :icon="'fa-solid fa-' + (isTableLocked ? 'lock' : 'lock-open')"/>
                    </button>
                </div>
            </div>
        </div>
        <div class="card-body p-0">
            <table class="table card-table mb-0">
                <tbody>
                    <tr v-for="participate in tableMembers" :draggable="participate ? !participate.lock : false" @dragstart="startDrag($event, participate ? participate.uuid : null)" @dragend="endDrag($event)">
                        <td v-if="participate" :key="participate.uuid">
                            <div class="hstack">
                                <div> {{ participate.name }} </div>
                                <button type="button" class="ms-auto btn btn-sm" @click="toggleLock(participate.uuid)" v-if="settingsStore.showOptions === true">
                                    <font-awesome-icon :class="'text-' + (settingsStore.darkModeToggle ? 'white' : 'black') + '-50'" :icon="'fa-solid fa-' + (participate.lock ? 'lock' : 'lock-open')"/>
                                </button>
                            </div>
                        </td>
                        <td v-else v-if="settingsStore.showAvailableSlots"><em>slot available</em></td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<style>
</style>
